import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import { secondAxios } from "../../../../../api/axios";
import { getFabrication } from "./Fabrication/api";
import { getItpsByProjectAndType } from "./ITP/api";

// Default context values
const defaultContextValue = {
  plantProjects: [],
  fabricationData: [],
  itpData: {},
  plant: false,
  plantId: undefined,
  activeProjectId: undefined,
  refreshPlantData: () => {},
};

// Create a context with default values
const PCSContext = createContext(defaultContextValue);

// Custom hook to use the PCSContext
export const usePCS = () => useContext(PCSContext);

// PCSContext Provider component
export const PCSProvider = ({ children, plant }) => {
  const { id, plantId, projectId } = useParams();
  const activeProjectId = projectId || id;

  // States to handle data
  const [plantProjects, setPlantProjects] = useState([]);
  const [fabricationData, setFabricationData] = useState([]);
  const [plantName, setPlantName] = useState("");

  const [itpData, setItpData] = useState({});

  const [projectFilter, setProjectFilter] = useState([]);
  const [SrvOptions, setSrvOptions] = useState([]);
  const [selectedSrvs, setSelectedSrvs] = useState([]);

  useEffect(() => {
    const getSrvs = async () => {
      const res = await secondAxios.get("/srv/plant/" + plantId);

      // console.log("plant srvs:", res.data);
      setSrvOptions(res.data ?? []);
    };
    getSrvs();
  }, []);

  useEffect(() => {}, [projectFilter]);
  // Fetch plant projects and data
  const fetchPlantData = useCallback(async () => {
    if (!plant || !plantId) {
      setPlantProjects([]);
      return;
    }

    try {
      const response = await secondAxios.get(`/plant/${plantId}`);
      console.log("response pcs plant", response);
      setPlantName(response.data?.name || "");
      const projectsRaw =
        response.data?.projects.filter(
          (p) => p.clientName === process.env.REACT_APP_CLIENT_NAME,
        ) ||
        (Array.isArray(response.data?.project)
          ? response.data.project
          : response.data?.project
            ? [response.data.project]
            : Array.isArray(response.data)
              ? response.data
              : []);

      // Filter projects by current client
      const filteredProjects = projectsRaw.filter(
        (p) => p.clientName === process.env.REACT_APP_CLIENT_NAME,
      );

      const normalizedProjects = [];

      // Normalize projects first
      filteredProjects.forEach((project) => {
        if (!project) return;
        const projectName =
          project.name || project.title || project.projectName;
        const projectId = project.id ?? project.projectId ?? projectName;
        normalizedProjects.push({
          id: projectId,
          name: projectName,
        });
      });

      // Fetch all fabrication data in parallel
      const fabricationPromises = normalizedProjects.map(async (proj) => {
        try {
          const fabData = await getFabrication(proj.id);
          return Array.isArray(fabData)
            ? fabData.map((item) => ({
                ...item,
                projectId: proj.id,
                projectName: proj.name,
              }))
            : [];
        } catch (error) {
          console.error(
            `Error fetching fabrication for project ${proj.id}:`,
            error,
          );
          return [];
        }
      });

      // Fetch all ITP data in parallel
      const itpTypes = [
        "PRE-FABRICATION",
        "MANUFACTURING & NDE",
        "PWHT & NDE POST PWHT",
        "HYDROSTATIC TEST",
        "FINAL PREPARATION",
      ];

      const itpPromises = normalizedProjects.flatMap((proj) =>
        itpTypes.map(async (type) => {
          try {
            const itps = await getItpsByProjectAndType(proj.id, type);
            return {
              type,
              data: Array.isArray(itps)
                ? itps.map((item) => ({
                    ...item,
                    projectId: proj.id,
                    projectName: proj.name,
                  }))
                : [],
            };
          } catch (error) {
            console.error(
              `Error fetching ITPs for project ${proj.id}, type ${type}:`,
              error,
            );
            return { type, data: [] };
          }
        }),
      );

      // Wait for all promises to resolve in parallel
      const [fabricationResults, itpResults] = await Promise.all([
        Promise.all(fabricationPromises),
        Promise.all(itpPromises),
      ]);

      // Combine fabrication data
      const allFabrication = fabricationResults.flat();

      // Combine ITP data by type
      const allItps = {};
      itpResults.forEach(({ type, data }) => {
        if (!allItps[type]) {
          allItps[type] = [];
        }
        allItps[type].push(...data);
      });

      setPlantProjects(normalizedProjects);
      setFabricationData(allFabrication);
      setItpData(allItps);
    } catch (error) {
      console.error("Failed to load plant PCS data", error);
      setPlantProjects([]);
      setFabricationData([]);
      setItpData({});
    }
  }, [plant, plantId]);

  useEffect(() => {
    fetchPlantData();
  }, [fetchPlantData]);

  // Provide all states and functions through the context
  return (
    <PCSContext.Provider
      value={{
        plantProjects,
        fabricationData,

        projectFilter,
        setProjectFilter,

        SrvOptions,
        selectedSrvs,
        setSelectedSrvs,

        itpData,
        plant,
        plantId,
        plantName,
        activeProjectId,
        refreshPlantData: fetchPlantData,
      }}
    >
      {children}
    </PCSContext.Provider>
  );
};
